import { useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import {
	MANAGE_SITES_STEP,
	ADD_USER_STEP,
	CANPARK_MONITOR_STEP,
	REGISTER_PLATE_STEP,
} from './tourRegistry';

// Config per tour whose menu steps live in the sidebar. Each keeps the pane open
// while its "open group" / "click item" steps show, and auto-advances from the
// group step to the item step once the item appears in the DOM (the group's
// Collapse uses unmountOnExit, so the child only exists after expansion).
interface SidebarTourConfig {
	openGroupStep: number;
	clickItemStep: number;
	itemSelector: string;
}

const SIDEBAR_TOURS: Record<string, SidebarTourConfig> = {
	canpark_manage_sites: {
		openGroupStep: MANAGE_SITES_STEP.OPEN_CANPARK,
		clickItemStep: MANAGE_SITES_STEP.MANAGE_SITES_ITEM,
		itemSelector: '[data-tour="manage-sites-menu-item"]',
	},
	canpark_monitor_site: {
		openGroupStep: CANPARK_MONITOR_STEP.OPEN_CANPARK,
		clickItemStep: CANPARK_MONITOR_STEP.MANAGE_SITES_ITEM,
		itemSelector: '[data-tour="manage-sites-menu-item"]',
	},
	canpark_register_plate: {
		openGroupStep: REGISTER_PLATE_STEP.OPEN_CANPARK,
		clickItemStep: REGISTER_PLATE_STEP.MANAGE_SITES_ITEM,
		itemSelector: '[data-tour="manage-sites-menu-item"]',
	},
	canapps_add_user: {
		openGroupStep: ADD_USER_STEP.OPEN_CANAPPS,
		clickItemStep: ADD_USER_STEP.USERS_ITEM,
		itemSelector: '[data-tour="users-menu-item"]',
	},
};

// Runs in SharedLayout (which owns the sidebar open state). For whichever
// sidebar-based tour is active it forces the pane open during the two menu steps
// and advances "open group" -> "click item" once the item is in the DOM.
export const useSidebarTourControl = (openSidebar: () => void) => {
	const { isOpen, currentStep, setCurrentStep } = useTour();

	// Keep the pane open while either menu step is showing.
	useEffect(() => {
		if (!isOpen) return;
		const cfg = SIDEBAR_TOURS[getActiveTourKey() ?? ''];
		if (!cfg) return;
		if (
			currentStep === cfg.openGroupStep ||
			currentStep === cfg.clickItemStep
		) {
			openSidebar();
		}
	}, [isOpen, currentStep, openSidebar]);

	// Advance once the menu item is in the DOM (its group has been expanded).
	useEffect(() => {
		if (!isOpen) return;
		const cfg = SIDEBAR_TOURS[getActiveTourKey() ?? ''];
		if (!cfg || currentStep !== cfg.openGroupStep) return;

		const found = () => !!document.querySelector(cfg.itemSelector);

		if (found()) {
			setCurrentStep(cfg.clickItemStep);
			return;
		}

		const observer = new MutationObserver(() => {
			if (found()) setCurrentStep(cfg.clickItemStep);
		});
		observer.observe(document.body, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [isOpen, currentStep, setCurrentStep]);
};
